import Card from "@mui/material/Card"
import { Skeleton, Box, Stack, Typography } from "@mui/material"
import { AdxChart } from "../../components/charts/AdxChart"
import { BollingerBandsChart } from "../../components/charts/BollingerBandsChart"
import { CustomChart } from "../../components/charts/CustomChart"
import { EMASMAChart } from "../../components/charts/EMASMAChart"
import { MACDChart } from "../../components/charts/MACDChart"
import { MFIChart } from "../../components/charts/MFIChart"
import { ObvChart } from "../../components/charts/ObvChart"
import { RSIChart } from "../../components/charts/RSIChart"
import { RSICrossOver } from "../../components/charts/RSIMAChart"
import { SMAChart } from "../../components/charts/SMAChart"
import { TripleEmaChart } from "../../components/charts/TripleEmaChart"
import { TripleRSI } from "../../components/charts/TripleRSI"
import { VolumeProfileChart } from "../../components/charts/VolumeProfileChart"
import { VWAPChart } from "../../components/charts/VWAPChart"
import { User } from "../../interface/User"
import { useEffect, useState } from "react"
import { MarketChart } from "../../components/charts/MarketChart"
import { TabsV2 } from "./TabsV2"
import { alpha, useTheme } from "@mui/material/styles"
import { dashboardMutedTextSx, getDashboardPanelSx } from "./dashboardStyles"

interface ChartV2SectionProps
{
  loading: boolean
  user: User
}

const KEY_CHART_INDEX = "KEY_CHART_INDEX"

export const ChartV2Section = ({ loading, user }: ChartV2SectionProps) => {
  const theme = useTheme()

  const [ tabIndex, setTabIndex ] = useState(8)

  useEffect(() => {

    const savedIndex = localStorage.getItem(KEY_CHART_INDEX)
    setTabIndex(Number(savedIndex) || 8)

  }, [ loading ])

  const handleChangeTab = (index: number) => {
    localStorage.setItem(KEY_CHART_INDEX, String(index))
    setTabIndex(index)
  }

  return (
    <Card sx={{ ...getDashboardPanelSx(theme, 600), p: 3 }} elevation={0}>
     { loading && <>
      <Skeleton height={28} width="30%"/>
      <Skeleton height={64} sx={{ mt: 2 }} width="90%" />
      <Skeleton height={420} sx={{ mt: 2 }} width="100%" />

     </>}

    { !loading && user && user.tradingPair && user.chartInterval && 
  <>
  <Stack spacing={2.5} sx={{ position: "relative", zIndex: 1 }}>
    <Box>
      <Typography variant="overline" sx={{ letterSpacing: 1.5, ...dashboardMutedTextSx(theme) }}>
        Strategy Console
      </Typography>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }} spacing={1.5}>
        <Typography sx={{ fontSize: { xs: 24, md: 30 }, lineHeight: 1.15 }}>
          {user.tradingPair} on {user.chartInterval}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            px: 1.5,
            py: 0.75,
            borderRadius: 999,
            backgroundColor: alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.06 : 0.62),
            ...dashboardMutedTextSx(theme),
          }}
        >
          Switch indicator presets without leaving the dashboard
        </Typography>
      </Stack>
    </Box>

    <TabsV2 tabIndex={tabIndex} handleChangeTab={handleChangeTab} />

    <Box
      sx={{
        p: { xs: 1, md: 1.5 },
        borderRadius: "12px",
        border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.12)}`,
        backgroundColor: alpha(theme.palette.common.black, theme.palette.mode === "dark" ? 0.14 : 0.02),
        minHeight: 470,
      }}
    >
      { tabIndex === 0 && <MACDChart chartInterval={user.chartInterval || "1m"} tradingPair={user.tradingPair} /> }
      { tabIndex === 1 && <EMASMAChart emaType={user.emaType} chartInterval={user.chartInterval || "1m"} tradingPair={user.tradingPair} emaShortPeriod={user.emaShortPeriod || 8} emaLongPeriod={user.emaLongPeriod || 20}/> }
      { tabIndex === 2 && <BollingerBandsChart chartInterval={user.chartInterval || "1m"} tradingPair={user.tradingPair}/> }
      { tabIndex === 3 && <RSIChart chartInterval={user.chartInterval || "1m"} tradingPair={user.tradingPair} rsiThresholdBuy={user.rsiThresholdBuy} rsiThresholdSell={user.rsiThresholdSell} /> }
      { tabIndex === 4 && <MFIChart chartInterval={user.chartInterval || "1m"} tradingPair={user.tradingPair} mfiThresholdBuy={user.mfiThresholdBuy} mfiThresholdSell={user.mfiThresholdSell} /> }
      { tabIndex === 5 && <SMAChart period={10} chartInterval={user.chartInterval || "1m"} tradingPair={user.tradingPair} /> }
      { tabIndex === 6 && <VWAPChart chartInterval={user.chartInterval || "1m"} tradingPair={user.tradingPair} /> }
      { tabIndex === 7 && <CustomChart chartInterval={user.chartInterval || "1m"} tradingPair={user.tradingPair} short={user.tripleEmaShortPeriod || 8} medium={user.tripleEmaMediumPeriod || 13} long={user.tripleEmaLongPeriod || 21}/> }
      { tabIndex === 8 && <TripleEmaChart chartInterval={user.chartInterval || "1m"} tradingPair={user.tradingPair} emaShortPeriod={user.tripleEmaShortPeriod || 5} emaMediumPeriod={user.tripleEmaMediumPeriod || 8} emaLongPeriod={user.tripleEmaLongPeriod || 13} volumeShort={user.shortVolumeCrossover || 9} volumeLong={user.longVolumeCrossover || 21}/> }
      { tabIndex === 9 && <RSICrossOver chartInterval={user.chartInterval || "1m"} tradingPair={user.tradingPair} rsiShort={user.rsiShort} rsiMedium={user.rsiMedium} rsiLong={user.rsiLong} /> }
      { tabIndex === 10 && <AdxChart period={14} chartInterval={user.chartInterval || "1m"} tradingPair={user.tradingPair} /> }
      { tabIndex === 11 && <TripleRSI tradingPair={user.tradingPair} /> }
      { tabIndex === 12 && <VolumeProfileChart tradingPair={user.tradingPair} chartInterval={user.chartInterval || "1m"} bars={user.vpNumberOfBars || 14}/> }
      { tabIndex === 13 && <ObvChart tradingPair={user.tradingPair} chartInterval={user.chartInterval || "1m"} longPeriod={user.obvLongPeriod || 21} shortPeriod={user.obvShortPeriod || 9}/> }
      { tabIndex === 14 && <MarketChart chartInterval={user.chartInterval || "1m"} tradingPair={user.tradingPair}/> }
    </Box>
  </Stack>


</> }

    
  </Card>
  )
}
